'use client';

import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, Clock, AlertTriangle, Shield, Calendar, Heart, Utensils, Pill, Phone } from 'lucide-react';
import { useTranslation } from "@/hooks/useTranslation";
import { convertToLocaleNumber } from "@/lib/i18n/numbers";
import { siteConfig } from "@/lib/site-config";

function highlightWord(title: string, word: string, className: string = "text-gold"): React.ReactNode {
    if (!word) return title;
    const normalizedTitle = title.normalize('NFC').toLowerCase();
    const normalizedWord = word.normalize('NFC').toLowerCase();
    const idx = normalizedTitle.indexOf(normalizedWord);
    if (idx !== -1) {
        const matched = title.slice(idx, idx + word.length);
        return (
            <>
                {title.slice(0, idx)}
                <span className={className}>{matched}</span>
                {title.slice(idx + word.length)}
            </>
        );
    }
    return title;
}

export default function PreparationRecoverySection() {
    const { t, isRTL, locale } = useTranslation("all-on-4");

    const preparationSteps = [
        {
            icon: <Calendar className="w-6 h-6 text-gold" />,
            title: t("preparationRecovery.preparation.steps.twoWeeks.title"),
            items: Array.isArray(t("preparationRecovery.preparation.steps.twoWeeks.items", { returnObjects: true })) 
                ? t("preparationRecovery.preparation.steps.twoWeeks.items", { returnObjects: true }) as string[]
                : []
        },
        {
            icon: <Heart className="w-6 h-6 text-gold" />,
            title: t("preparationRecovery.preparation.steps.oneWeek.title"),
            items: Array.isArray(t("preparationRecovery.preparation.steps.oneWeek.items", { returnObjects: true })) 
                ? t("preparationRecovery.preparation.steps.oneWeek.items", { returnObjects: true }) as string[]
                : []
        },
        {
            icon: <Clock className="w-6 h-6 text-gold" />,
            title: t("preparationRecovery.preparation.steps.dayBefore.title"),
            items: Array.isArray(t("preparationRecovery.preparation.steps.dayBefore.items", { returnObjects: true })) 
                ? t("preparationRecovery.preparation.steps.dayBefore.items", { returnObjects: true }) as string[]
                : []
        }
    ];

    const recoveryTimeline = [
        {
            period: t("preparationRecovery.recovery.timeline.immediate.period"),
            title: t("preparationRecovery.recovery.timeline.immediate.title"),
            description: t("preparationRecovery.recovery.timeline.immediate.description"),
            activities: Array.isArray(t("preparationRecovery.recovery.timeline.immediate.activities", { returnObjects: true })) 
                ? t("preparationRecovery.recovery.timeline.immediate.activities", { returnObjects: true }) as string[]
                : [],
            restrictions: Array.isArray(t("preparationRecovery.recovery.timeline.immediate.restrictions", { returnObjects: true })) 
                ? t("preparationRecovery.recovery.timeline.immediate.restrictions", { returnObjects: true }) as string[]
                : []
        },
        {
            period: t("preparationRecovery.recovery.timeline.earlyHealing.period"),
            title: t("preparationRecovery.recovery.timeline.earlyHealing.title"),
            description: t("preparationRecovery.recovery.timeline.earlyHealing.description"),
            activities: Array.isArray(t("preparationRecovery.recovery.timeline.earlyHealing.activities", { returnObjects: true })) 
                ? t("preparationRecovery.recovery.timeline.earlyHealing.activities", { returnObjects: true }) as string[]
                : [],
            restrictions: Array.isArray(t("preparationRecovery.recovery.timeline.earlyHealing.restrictions", { returnObjects: true })) 
                ? t("preparationRecovery.recovery.timeline.earlyHealing.restrictions", { returnObjects: true }) as string[]
                : []
        },
        {
            period: t("preparationRecovery.recovery.timeline.progressive.period"),
            title: t("preparationRecovery.recovery.timeline.progressive.title"),
            description: t("preparationRecovery.recovery.timeline.progressive.description"),
            activities: Array.isArray(t("preparationRecovery.recovery.timeline.progressive.activities", { returnObjects: true })) 
                ? t("preparationRecovery.recovery.timeline.progressive.activities", { returnObjects: true }) as string[]
                : [],
            restrictions: Array.isArray(t("preparationRecovery.recovery.timeline.progressive.restrictions", { returnObjects: true })) 
                ? t("preparationRecovery.recovery.timeline.progressive.restrictions", { returnObjects: true }) as string[]
                : []
        },
        {
            period: t("preparationRecovery.recovery.timeline.finalIntegration.period"),
            title: t("preparationRecovery.recovery.timeline.finalIntegration.title"),
            description: t("preparationRecovery.recovery.timeline.finalIntegration.description"),
            activities: Array.isArray(t("preparationRecovery.recovery.timeline.finalIntegration.activities", { returnObjects: true })) 
                ? t("preparationRecovery.recovery.timeline.finalIntegration.activities", { returnObjects: true }) as string[]
                : [],
            restrictions: Array.isArray(t("preparationRecovery.recovery.timeline.finalIntegration.restrictions", { returnObjects: true })) 
                ? t("preparationRecovery.recovery.timeline.finalIntegration.restrictions", { returnObjects: true }) as string[]
                : []
        }
    ];

    const nutritionGuidelines = {
        immediate: {
            title: t("preparationRecovery.nutrition.immediate.title"),
            foods: Array.isArray(t("preparationRecovery.nutrition.immediate.foods", { returnObjects: true })) 
                ? t("preparationRecovery.nutrition.immediate.foods", { returnObjects: true }) as string[]
                : []
        },
        early: {
            title: t("preparationRecovery.nutrition.early.title"),
            foods: Array.isArray(t("preparationRecovery.nutrition.early.foods", { returnObjects: true })) 
                ? t("preparationRecovery.nutrition.early.foods", { returnObjects: true }) as string[]
                : []
        },
        progressive: {
            title: t("preparationRecovery.nutrition.progressive.title"),
            foods: Array.isArray(t("preparationRecovery.nutrition.progressive.foods", { returnObjects: true })) 
                ? t("preparationRecovery.nutrition.progressive.foods", { returnObjects: true }) as string[]
                : []
        }
    };

    const warningSignsData = [
        {
            icon: <AlertTriangle className="w-5 h-5 text-red-500" />,
            title: t("preparationRecovery.warnings.excessiveBleeding.title"),
            description: t("preparationRecovery.warnings.excessiveBleeding.description")
        },
        {
            icon: <AlertTriangle className="w-5 h-5 text-red-500" />,
            title: t("preparationRecovery.warnings.severePain.title"),
            description: t("preparationRecovery.warnings.severePain.description")
        },
        {
            icon: <AlertTriangle className="w-5 h-5 text-red-500" />,
            title: t("preparationRecovery.warnings.infection.title"),
            description: t("preparationRecovery.warnings.infection.description")
        },
        {
            icon: <AlertTriangle className="w-5 h-5 text-red-500" />,
            title: t("preparationRecovery.warnings.implantMovement.title"),
            description: t("preparationRecovery.warnings.implantMovement.description")
        }
    ];

    return (
        <section id="preparation" className="py-16 lg:py-24 bg-slate-50 dark:bg-navy-950 min-h-[800px]">
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {highlightWord(t("preparationRecovery.title"), t("preparationRecovery.highlightWord"))}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t("preparationRecovery.subtitle")}
                    </p>
                </div>

                <Tabs defaultValue="preparation" className="w-full">
                    <TabsList className={`grid w-full grid-cols-4 lg:w-fit mx-auto mb-12 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        {isRTL ? (
                            <>
                                <TabsTrigger value="warnings">{t("preparationRecovery.tabs.warnings")}</TabsTrigger>
                                <TabsTrigger value="nutrition">{t("preparationRecovery.tabs.nutrition")}</TabsTrigger>
                                <TabsTrigger value="recovery">{t("preparationRecovery.tabs.recovery")}</TabsTrigger>
                                <TabsTrigger value="preparation">{t("preparationRecovery.tabs.preparation")}</TabsTrigger>
                            </>
                        ) : (
                            <>
                                <TabsTrigger value="preparation">{t("preparationRecovery.tabs.preparation")}</TabsTrigger>
                                <TabsTrigger value="recovery">{t("preparationRecovery.tabs.recovery")}</TabsTrigger>
                                <TabsTrigger value="nutrition">{t("preparationRecovery.tabs.nutrition")}</TabsTrigger>
                                <TabsTrigger value="warnings">{t("preparationRecovery.tabs.warnings")}</TabsTrigger>
                            </>
                        )}
                    </TabsList>

                    <TabsContent value="preparation" className="space-y-8 min-h-[600px]">
                        <div className="text-center mb-12">
                            <h3 className="text-2xl lg:text-3xl font-bold text-navy dark:text-white mb-4">
                                {highlightWord(t("preparationRecovery.preparation.title"), t("preparationRecovery.preparation.highlightWord"))}
                            </h3>
                            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                                {t("preparationRecovery.preparation.subtitle")}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {(isRTL ? [...preparationSteps].reverse() : preparationSteps).map((step, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1, duration: 0.5 }}
                                    viewport={{ once: true }}
                                >
                                    <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow duration-300">
                                        <CardContent className={`p-6 ${isRTL ? 'text-right' : ''}`}>
                                            <div className={`flex items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <div className={`w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center ${isRTL ? 'ml-4' : 'mr-4'}`}>
                                                    {step.icon}
                                                </div>
                                                <h4 className={`text-xl font-semibold text-navy dark:text-white ${isRTL ? 'text-right' : ''}`}>
                                                    {step.title}
                                                </h4>
                                            </div>
                                            <ul className="space-y-3">
                                                {step.items.map((item, idx) => (
                                                    <li key={idx} className={`flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                        <CheckCircle className={`w-4 h-4 text-gold mt-0.5 shrink-0 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                                                        <span className={`text-slate-600 dark:text-slate-300 text-sm ${isRTL ? 'text-right' : ''}`}>
                                                            {item}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="recovery" className="space-y-8 min-h-[600px]">
                        <div className="text-center mb-12">
                            <h3 className="text-2xl lg:text-3xl font-bold text-navy dark:text-white mb-4">
                                {highlightWord(t("preparationRecovery.recovery.title"), t("preparationRecovery.recovery.highlightWord"))}
                            </h3>
                            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                                {t("preparationRecovery.recovery.subtitle")}
                            </p>
                        </div>

                        <div className="flex gap-4 overflow-x-auto lg:overflow-x-visible">
                            {(isRTL ? [...recoveryTimeline].reverse() : recoveryTimeline).map((phase, index) => {
                                const displayNumber = isRTL ? recoveryTimeline.length - index : index + 1;
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1, duration: 0.5 }}
                                        viewport={{ once: true }}
                                        className="flex-none w-72 lg:flex-1"
                                    >
                                        <Card className="h-full border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow duration-300 min-h-[480px]">
                                            <CardContent className={`p-5 ${isRTL ? 'text-right' : ''}`}>
                                                <div className={`${isRTL ? 'text-right' : 'text-center'} mb-4`}>
                                                    <div className={`w-10 h-10 bg-gold text-navy rounded-lg flex items-center justify-center font-bold mb-3 text-sm ${isRTL ? 'ml-auto' : 'mx-auto'}`}>
                                                        {convertToLocaleNumber(displayNumber, locale)}
                                                    </div>
                                                <h4 className={`text-base font-semibold text-navy dark:text-white mb-1 ${isRTL ? 'text-right' : ''}`}>
                                                    {phase.period}
                                                </h4>
                                                <p className={`text-gold font-medium text-xs ${isRTL ? 'text-right' : ''}`}>
                                                    {phase.title}
                                                </p>
                                            </div>

                                            <p className={`text-slate-600 dark:text-slate-300 text-xs mb-4 ${isRTL ? 'text-right' : 'text-center'}`}>
                                                {phase.description}
                                            </p>

                                            <div className="space-y-3">
                                                <div>
                                                    <h5 className={`font-semibold text-navy dark:text-white mb-2 text-xs ${isRTL ? 'text-right' : ''}`}>
                                                        {t("preparationRecovery.recovery.whatToDo")}:
                                                    </h5>
                                                    <ul className="space-y-1">
                                                        {phase.activities.map((activity, idx) => (
                                                            <li key={idx} className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                                <CheckCircle className="w-3 h-3 text-green-600 mt-0.5 shrink-0" />
                                                                <span className={`text-slate-600 dark:text-slate-300 text-xs leading-tight ${isRTL ? 'text-right' : ''}`}>
                                                                    {activity}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>

                                                <div>
                                                    <h5 className={`font-semibold text-navy dark:text-white mb-2 text-xs ${isRTL ? 'text-right' : ''}`}>
                                                        {t("preparationRecovery.recovery.avoid")}:
                                                    </h5>
                                                    <ul className="space-y-1">
                                                        {phase.restrictions.map((restriction, idx) => (
                                                            <li key={idx} className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                                <AlertTriangle className="w-3 h-3 text-orange-500 mt-0.5 shrink-0" />
                                                                <span className={`text-slate-600 dark:text-slate-300 text-xs leading-tight ${isRTL ? 'text-right' : ''}`}>
                                                                    {restriction}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                                );
                            })}
                        </div>
                    </TabsContent>

                    <TabsContent value="nutrition" className="space-y-8 min-h-[600px]">
                        <div className="text-center mb-12">
                            <h3 className="text-2xl lg:text-3xl font-bold text-navy dark:text-white mb-4">
                                {highlightWord(t("preparationRecovery.nutrition.title"), t("preparationRecovery.nutrition.highlightWord"))}
                            </h3>
                            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                                {t("preparationRecovery.nutrition.subtitle")}
                            </p>
                        </div>

                        <div className="grid lg:grid-cols-3 gap-8">
                            {(isRTL ? Object.entries(nutritionGuidelines).reverse() : Object.entries(nutritionGuidelines)).map(([key, phase], index) => (
                                <motion.div
                                    key={key}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1, duration: 0.5 }}
                                    viewport={{ once: true }}
                                >
                                    <Card className="h-full border-slate-200 dark:border-slate-700">
                                        <CardContent className={`p-6 ${isRTL ? 'text-right' : ''}`}>
                                            <div className={`flex items-center gap-3 mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <Utensils className="w-6 h-6 text-gold" />
                                                <h4 className={`text-lg font-semibold text-navy dark:text-white ${isRTL ? 'text-right' : ''}`}>
                                                    {phase.title}
                                                </h4>
                                            </div>
                                            <ul className="space-y-2">
                                                {phase.foods.map((food, idx) => (
                                                    <li key={idx} className={`flex items-start gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                        <CheckCircle className="w-3 h-3 text-gold mt-1 shrink-0" />
                                                        <span className={`text-slate-600 dark:text-slate-300 text-sm ${isRTL ? 'text-right' : ''}`}>
                                                            {food}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="warnings" className="space-y-8 min-h-[600px]">
                        <div className="text-center mb-12">
                            <h3 className="text-2xl lg:text-3xl font-bold text-navy dark:text-white mb-4">
                                {highlightWord(t("preparationRecovery.warnings.title"), t("preparationRecovery.warnings.highlightWord"))}
                            </h3>
                            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
                                {t("preparationRecovery.warnings.subtitle")}
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mb-12">
                            {warningSignsData.map((warning, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: index * 0.1, duration: 0.5 }}
                                    viewport={{ once: true }}
                                >
                                    <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                                        <CardContent className={`p-6 ${isRTL ? 'text-right' : ''}`}>
                                            <div className={`flex items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <div className={`w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-lg flex items-center justify-center shrink-0 ${isRTL ? 'ml-4' : 'mr-4'}`}>
                                                    {warning.icon}
                                                </div>
                                                <div className={isRTL ? 'text-right' : ''}>
                                                    <h4 className={`text-lg font-semibold text-red-700 dark:text-red-300 mb-2 ${isRTL ? 'text-right' : ''}`}>
                                                        {warning.title}
                                                    </h4>
                                                    <p className={`text-red-600 dark:text-red-400 ${isRTL ? 'text-right' : ''}`}>
                                                        {warning.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        <Card className="bg-gold/10 border-gold/30">
                            <CardContent className="p-8 text-center">
                                <Phone className="w-12 h-12 text-gold mx-auto mb-4" />
                                <h4 className="text-xl font-bold text-navy dark:text-white mb-4">
                                    {t("preparationRecovery.emergency.title")}
                                </h4>
                                <p className="text-slate-600 dark:text-slate-300 mb-6">
                                    {t("preparationRecovery.emergency.description")}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <div className="text-center">
                                        <p className="font-semibold text-navy dark:text-white">{t("preparationRecovery.emergency.whatsapp")}:</p>
                                        <p className="text-gold font-medium">{t("preparationRecovery.emergency.whatsappNumber", { phone: siteConfig.contact.phone })}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="font-semibold text-navy dark:text-white">{t("preparationRecovery.emergency.email")}:</p>
                                        <p className="text-gold font-medium">{t("preparationRecovery.emergency.emailAddress", { email: siteConfig.contact.email })}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </section>
    );
} 